import { ChatMarkdown } from "@rakazo/chat-ui/web";
import { BotAvatar, Button, GroupAvatar, type GroupAvatarMember } from "@rakazo/ui-web";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

/**
 * Peer event in a bot's own chat. The label opens the exchange; the toggle shows what was said.
 * `replies` uses the same toggle for the peer run's folded replies.
 */
export function CollaborationMarker({
  ariaLabel,
  body,
  color,
  identity,
  label,
  onClick,
  replies,
}: {
  ariaLabel: string;
  /** The message this bot sent or received. Hidden until the toggle is opened. */
  body?: string;
  color: string;
  identity: string;
  label: string;
  onClick: () => void;
  replies?: { expanded: boolean; label: string; onToggle: () => void };
}) {
  const [bodyOpen, setBodyOpen] = useState(false);
  const text = body?.trim() ?? "";
  const expanded = replies ? replies.expanded : bodyOpen;
  const toggleLabel =
    replies?.label ?? (expanded ? `Collapse ${ariaLabel}` : `Expand ${ariaLabel}`);
  const onToggle = replies?.onToggle ?? (() => setBodyOpen((open) => !open));
  const showToggle = Boolean(replies) || text.length > 0;
  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="flex justify-start">
        <button
          type="button"
          data-testid="peer-receipt-chip"
          aria-label={ariaLabel}
          onClick={onClick}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground/75"
        >
          <BotAvatar color={color} identity={identity} size={16} />
          <span dir="auto" className="truncate">
            {label}
          </span>
        </button>
        {showToggle ? (
          <Button
            variant="ghost"
            size="icon-sm"
            data-testid="peer-replies-toggle"
            aria-expanded={expanded}
            aria-label={toggleLabel}
            onClick={onToggle}
            className="rounded-full text-muted-foreground"
          >
            <ChevronDown
              size={14}
              strokeWidth={1.8}
              className={
                expanded
                  ? "size-3.5 transition-transform"
                  : "size-3.5 -rotate-90 transition-transform"
              }
              aria-hidden="true"
            />
          </Button>
        ) : null}
      </div>
      {expanded && text ? (
        <div
          data-testid="peer-message-body"
          className="max-w-full rounded-2xl bg-muted px-3.5 py-2.5 text-[14.5px] leading-[1.5] text-foreground/90"
          dir="auto"
        >
          <ChatMarkdown>{text}</ChatMarkdown>
        </div>
      ) : null}
    </div>
  );
}

/** The working bot with what it is doing right now, such as its current tool call. */
export function ActiveBotGlyph({
  bots,
  label,
  elapsed,
}: {
  bots: GroupAvatarMember[];
  label: ReactNode;
  elapsed?: string | null;
}) {
  const textLabel = typeof label === "string";
  return (
    <div
      role="status"
      className={`flex min-h-10 min-w-0 gap-2.5 px-1 ${textLabel ? "items-center" : "items-start"}`}
    >
      <GroupAvatar members={bots} size={28} />
      {textLabel ? (
        <span
          dir="auto"
          className="min-w-0 truncate text-[15px] text-muted-foreground text-shimmer"
        >
          {label}
        </span>
      ) : (
        <div className="min-w-0 flex-1 pt-1">{label}</div>
      )}
      {elapsed ? (
        <span className="shrink-0 text-[13px] text-muted-foreground/80 tabular-nums">
          {elapsed}
        </span>
      ) : null}
    </div>
  );
}
